import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { getTokenSecondsRemaining } from '@/lib/jwt';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Configuration
const REFRESH_BUFFER_SECONDS = 120; // Refresh 2 min before expiry
const REFRESH_RETRY_MS = 15_000; // Retry failed refresh after 15s
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 min idle → show warning
const WARNING_COUNTDOWN_SECONDS = 60; // 60 sec countdown before logout
const ACTIVITY_THROTTLE_MS = 1000; // Throttle activity tracking to 1/sec

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart',
];

interface SessionState {
  showWarning: boolean;
  countdownSeconds: number;
}

export function useSessionManager() {
  const [sessionState, setSessionState] = useState<SessionState>({
    showWarning: false,
    countdownSeconds: WARNING_COUNTDOWN_SECONDS,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRefreshingRef = useRef(false);
  const lastThrottleRef = useRef(0);
  const refreshFailCountRef = useRef(0);

  // --- Token Refresh ---

  const doRefresh = useCallback(async (): Promise<boolean> => {
    // Another refresh is already in-flight — treat as success (don't consume retry budget)
    if (isRefreshingRef.current) return true;
    isRefreshingRef.current = true;

    try {
      // Include expired access token in Authorization header so backend can
      // preserve the `env` claim during token rotation (multi-env support).
      const expiredToken = useAuthStore.getState().accessToken;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (expiredToken) {
        headers['Authorization'] = `Bearer ${expiredToken}`;
      }

      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        useAuthStore.getState().setAccessToken(data.access_token);
        refreshFailCountRef.current = 0;
        return true;
      }

      if (res.status === 401 || res.status === 403) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
      return false;
    } catch (error) {
      console.error('[SessionManager] Token refresh error:', error);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const token = useAuthStore.getState().accessToken;
    if (!token) return;

    const secondsRemaining = getTokenSecondsRemaining(token);
    if (secondsRemaining <= 0) {
      doRefresh().then((ok) => {
        if (ok) {
          refreshFailCountRef.current = 0;
          scheduleRefresh();
        } else {
          // Retry with backoff instead of dying silently
          refreshFailCountRef.current += 1;
          if (refreshFailCountRef.current <= 5) {
            refreshTimerRef.current = setTimeout(() => scheduleRefresh(), REFRESH_RETRY_MS);
          } else {
            console.error('[SessionManager] Token refresh failed after 5 retries');
            setSessionState({ showWarning: true, countdownSeconds: WARNING_COUNTDOWN_SECONDS });
          }
        }
      });
      return;
    }

    // Schedule refresh REFRESH_BUFFER_SECONDS before expiry (min 5s)
    const refreshInMs = Math.max(
      (secondsRemaining - REFRESH_BUFFER_SECONDS) * 1000,
      5000,
    );

    refreshTimerRef.current = setTimeout(async () => {
      const ok = await doRefresh();
      if (ok) {
        refreshFailCountRef.current = 0;
        scheduleRefresh();
      } else {
        // Retry with backoff instead of dying silently
        refreshFailCountRef.current += 1;
        if (refreshFailCountRef.current <= 5) {
          refreshTimerRef.current = setTimeout(() => scheduleRefresh(), REFRESH_RETRY_MS);
        }
      }
    }, refreshInMs);
  }, [doRefresh]);

  // --- Idle Detection ---

  const performLogout = useCallback(() => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});

    useAuthStore.getState().logout();
    window.location.href = '/login';
  }, []);

  const resetIdleTimer = useCallback(() => {
    // Dismiss warning if showing
    setSessionState((prev) => {
      if (prev.showWarning) {
        return { showWarning: false, countdownSeconds: WARNING_COUNTDOWN_SECONDS };
      }
      return prev;
    });

    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      // User idle — show warning with countdown
      setSessionState({ showWarning: true, countdownSeconds: WARNING_COUNTDOWN_SECONDS });

      countdownTimerRef.current = setInterval(() => {
        setSessionState((prev) => {
          const next = prev.countdownSeconds - 1;
          if (next <= 0) {
            performLogout();
            return { showWarning: false, countdownSeconds: 0 };
          }
          return { ...prev, countdownSeconds: next };
        });
      }, 1000);
    }, IDLE_TIMEOUT_MS);
  }, [performLogout]);

  // Throttled activity handler
  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottleRef.current = now;
    resetIdleTimer();
  }, [resetIdleTimer]);

  // --- Tab visibility: refresh token before React Query refetches ---

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== 'visible') return;

    const { user, accessToken } = useAuthStore.getState();
    if (!user) return;

    // Reset fail counter on tab return — fresh start
    refreshFailCountRef.current = 0;

    // Check if token is expired or about to expire
    if (!accessToken) {
      doRefresh().then((ok) => { if (ok) scheduleRefresh(); });
      return;
    }

    const remaining = getTokenSecondsRemaining(accessToken);
    if (remaining < REFRESH_BUFFER_SECONDS) {
      doRefresh().then((ok) => { if (ok) scheduleRefresh(); });
    } else {
      // Token still valid — reschedule refresh timer (may have drifted while tab was hidden)
      scheduleRefresh();
    }

    // Reset idle timer on tab return (user is active)
    resetIdleTimer();
  }, [doRefresh, scheduleRefresh, resetIdleTimer]);

  // --- Lifecycle ---

  useEffect(() => {
    const { user, accessToken } = useAuthStore.getState();
    if (!user) return;

    if (accessToken) {
      scheduleRefresh();
    }

    resetIdleTimer();

    // Listen for activity
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Listen for tab visibility changes (refresh token when returning to tab)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for token changes (after login, after AuthInitializer refresh, etc.)
    const unsub = useAuthStore.subscribe((state, prevState) => {
      if (state.accessToken && state.accessToken !== prevState.accessToken) {
        refreshFailCountRef.current = 0;
        scheduleRefresh();
      }
    });

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unsub();
    };
  }, [scheduleRefresh, resetIdleTimer, handleActivity, handleVisibilityChange]);

  const dismissWarning = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  return {
    showWarning: sessionState.showWarning,
    countdownSeconds: sessionState.countdownSeconds,
    dismissWarning,
  };
}
